import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { Pill, Send, FileText, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuditLog } from "@/hooks/useAuditLog";

interface Pharmacy {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
}

interface Prescription {
  id: string;
  patient_id: string;
  peptide_name: string;
  dosage: string;
  frequency: string;
  quantity: string;
  refills: number;
  route: string;
  instructions: string | null;
  status: string;
  sent_at: string | null;
  created_at: string;
  pharmacy_id: string;
}

const ROUTES = [
  "Subcutaneous Injection",
  "Intramuscular Injection",
  "Oral",
  "Sublingual",
  "Intranasal",
  "Topical",
];

const FREQUENCIES = [
  "Once daily",
  "Twice daily",
  "Three times daily",
  "Once weekly",
  "Twice weekly",
  "Every other day",
  "As directed",
];

interface SendPrescriptionProps {
  patientId: string;
  assessmentId?: string;
  defaultPeptide?: string;
  /** When true, renders the form inline without a wrapping dialog */
  inline?: boolean;
  /** Called after a prescription is successfully created */
  onPrescriptionSent?: () => void;
}

const PrescriptionForm = ({
  patientId,
  assessmentId,
  defaultPeptide,
  onSuccess,
}: {
  patientId: string;
  assessmentId?: string;
  defaultPeptide?: string;
  onSuccess?: (sent: boolean) => void;
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { logAccess } = useAuditLog();
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState({
    pharmacy_id: "",
    peptide_name: defaultPeptide || "",
    dosage: "",
    frequency: "Once daily",
    quantity: "",
    refills: 0,
    route: "Subcutaneous Injection",
    instructions: "",
  });

  useEffect(() => {
    fetchPharmacies();
  }, []);

  const fetchPharmacies = async () => {
    const { data } = await (supabase as any)
      .from("partner_pharmacies_safe")
      .select("id, name, city, state")
      .order("name");
    if (data) setPharmacies(data as Pharmacy[]);
  };

  const handleSubmit = async (sendNow: boolean) => {
    if (!user || !form.pharmacy_id || !form.peptide_name || !form.dosage || !form.quantity) {
      toast({ title: "Missing fields", description: "Please fill in all required fields.", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    const prescriptionId = crypto.randomUUID();
    const status = sendNow ? "sent" : "draft";

    const { error } = await supabase.from("prescriptions").insert({
      id: prescriptionId,
      patient_id: patientId,
      provider_id: user.id,
      pharmacy_id: form.pharmacy_id,
      assessment_id: assessmentId || null,
      peptide_name: form.peptide_name,
      dosage: form.dosage,
      frequency: form.frequency,
      quantity: form.quantity,
      refills: form.refills,
      route: form.route,
      instructions: form.instructions || null,
      status,
      sent_at: sendNow ? new Date().toISOString() : null,
    });

    if (error) {
      toast({ title: "Error", description: "Failed to create prescription.", variant: "destructive" });
      setIsSubmitting(false);
      return;
    }

    if (sendNow) {
      const pharmacy = pharmacies.find(p => p.id === form.pharmacy_id);
      // Edge function looks up pharmacy email server-side using service role,
      // so we don't need to expose pharmacy contact details to the provider client.
      await supabase.functions.invoke("send-prescription", {
        body: {
          prescriptionId,
          pharmacyId: form.pharmacy_id,
          pharmacyName: pharmacy?.name,
        },
      });

      // Notify the patient (in-app)
      await supabase.from("notifications").insert({
        user_id: patientId,
        title: "Prescription Sent",
        message: `Your prescription for ${form.peptide_name} has been sent to ${pharmacy?.name || "your pharmacy"}.`,
        type: "prescription",
        related_id: prescriptionId,
      });
    }

    logAccess({
      action: sendNow ? "send_prescription" : "draft_prescription",
      resource_type: "prescription",
      resource_id: prescriptionId,
      metadata: { patient_id: patientId, pharmacy_id: form.pharmacy_id, peptide: form.peptide_name },
    });

    toast({
      title: sendNow ? "Prescription sent" : "Draft saved",
      description: sendNow ? "Prescription emailed to pharmacy." : "Prescription saved as draft.",
    });

    setForm({
      pharmacy_id: "",
      peptide_name: defaultPeptide || "",
      dosage: "",
      frequency: "Once daily",
      quantity: "",
      refills: 0,
      route: "Subcutaneous Injection",
      instructions: "",
    });

    onSuccess?.(sendNow);
    setIsSubmitting(false);
  };

  const selectedPharmacy = pharmacies.find(p => p.id === form.pharmacy_id);

  return (
    <div className="space-y-5">
      {/* Pharmacy Selection */}
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Partner Pharmacy *</Label>
        <Select value={form.pharmacy_id} onValueChange={(v) => setForm(prev => ({ ...prev, pharmacy_id: v }))}>
          <SelectTrigger>
            <SelectValue placeholder="Select a pharmacy" />
          </SelectTrigger>
          <SelectContent>
            {pharmacies.map(p => (
              <SelectItem key={p.id} value={p.id}>
                {p.name} — {p.city}, {p.state}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedPharmacy && (
          <p className="text-xs text-muted-foreground">
            📍 {selectedPharmacy.city}, {selectedPharmacy.state}
          </p>
        )}
        {pharmacies.length === 0 && (
          <p className="text-xs text-muted-foreground italic">No partner pharmacies configured. An admin must add them first.</p>
        )}
      </div>

      {/* Medication Details */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Peptide Name *</Label>
          <Input
            placeholder="e.g. Semaglutide"
            value={form.peptide_name}
            onChange={(e) => setForm(prev => ({ ...prev, peptide_name: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Dosage *</Label>
          <Input
            placeholder="e.g. 0.25mg"
            value={form.dosage}
            onChange={(e) => setForm(prev => ({ ...prev, dosage: e.target.value }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Frequency</Label>
          <Select value={form.frequency} onValueChange={(v) => setForm(prev => ({ ...prev, frequency: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {FREQUENCIES.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Route of Administration</Label>
          <Select value={form.route} onValueChange={(v) => setForm(prev => ({ ...prev, route: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROUTES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Quantity *</Label>
          <Input
            placeholder="e.g. 30 units"
            value={form.quantity}
            onChange={(e) => setForm(prev => ({ ...prev, quantity: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Refills</Label>
          <Select value={String(form.refills)} onValueChange={(v) => setForm(prev => ({ ...prev, refills: parseInt(v) }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {[0, 1, 2, 3, 4, 5, 6, 11].map(n => (
                <SelectItem key={n} value={String(n)}>{n === 0 ? "No refills" : `${n} refill${n > 1 ? "s" : ""}`}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-semibold">Special Instructions</Label>
        <Textarea
          placeholder="Additional instructions for the pharmacist..."
          value={form.instructions}
          onChange={(e) => setForm(prev => ({ ...prev, instructions: e.target.value }))}
          className="min-h-[80px]"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={() => handleSubmit(false)} disabled={isSubmitting} className="flex-1 gap-2">
          <Clock size={16} /> Save Draft
        </Button>
        <Button onClick={() => handleSubmit(true)} disabled={isSubmitting} className="flex-1 gap-2">
          <Send size={16} /> Send to Pharmacy
        </Button>
      </div>
    </div>
  );
};

const SendPrescription = ({ patientId, assessmentId, defaultPeptide, inline, onPrescriptionSent }: SendPrescriptionProps) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [recentRx, setRecentRx] = useState<Prescription[]>([]);
  const [decisionStatus, setDecisionStatus] = useState<string | null>(null);

  useEffect(() => {
    if (patientId && user) {
      fetchRecentPrescriptions();
      fetchLatestDecision();
    }
  }, [patientId, user]);

  const fetchRecentPrescriptions = async () => {
    const { data } = await supabase
      .from("prescriptions")
      .select("*")
      .eq("patient_id", patientId)
      .order("created_at", { ascending: false })
      .limit(5);
    if (data) setRecentRx(data as Prescription[]);
  };

  const fetchLatestDecision = async () => {
    const { data } = await supabase
      .from("health_assessments")
      .select("status")
      .eq("user_id", patientId)
      .order("created_at", { ascending: false })
      .limit(1);
    setDecisionStatus(data?.[0]?.status ?? null);
  };

  const handleSuccess = (sent: boolean) => {
    fetchRecentPrescriptions();
    if (sent) {
      setOpen(false);
      onPrescriptionSent?.();
    }
  };

  // PRD §4 — Rx form is gated behind explicit "approved" decision in the
  // Clinical Decision panel. RLS still enforces has_completed_consultation
  // server-side; this is the UI-level gate so providers don't accidentally
  // skip the decision step.
  const isApproved = decisionStatus === "approved";

  // Inline mode: render form directly (gated)
  if (inline) {
    if (!isApproved) {
      return (
        <Card className="border-dashed border-border bg-muted/30">
          <CardContent className="p-6 text-center text-sm text-muted-foreground">
            <Pill size={20} className="mx-auto mb-2 opacity-60" />
            <p className="font-medium text-foreground">Approve treatment to write a prescription</p>
            <p className="text-xs mt-1">Use the <span className="font-medium">Clinical Decision</span> panel above to record your "Approve treatment" decision before prescribing.</p>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="space-y-4">
        <PrescriptionForm
          patientId={patientId}
          assessmentId={assessmentId}
          defaultPeptide={defaultPeptide}
          onSuccess={handleSuccess}
        />

        {recentRx.length > 0 && (
          <Card className="border-border bg-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">Recent Prescriptions</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Peptide</TableHead>
                    <TableHead>Dosage</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentRx.map(rx => (
                    <TableRow key={rx.id}>
                      <TableCell className="text-sm">{new Date(rx.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-sm font-medium">{rx.peptide_name}</TableCell>
                      <TableCell className="text-sm">{rx.dosage} · {rx.frequency}</TableCell>
                      <TableCell>
                        <Badge variant={rx.status === "sent" ? "default" : "secondary"} className="capitalize text-xs">
                          {rx.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Dialog mode (default)
  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="gap-2" size="sm" disabled={!isApproved} title={!isApproved ? "Approve treatment in the Clinical Decision panel first" : undefined}>
            <Pill size={16} /> {isApproved ? "Write Prescription" : "Approve to prescribe"}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <FileText size={20} className="text-primary" />
              New Prescription
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <PrescriptionForm
              patientId={patientId}
              assessmentId={assessmentId}
              defaultPeptide={defaultPeptide}
              onSuccess={handleSuccess}
            />
          </div>
        </DialogContent>
      </Dialog>

      {recentRx.length > 0 && (
        <Card className="mt-4 border-border bg-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Recent Prescriptions</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Peptide</TableHead>
                  <TableHead>Dosage</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentRx.map(rx => (
                  <TableRow key={rx.id}>
                    <TableCell className="text-sm">{new Date(rx.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-sm font-medium">{rx.peptide_name}</TableCell>
                    <TableCell className="text-sm">{rx.dosage} · {rx.frequency}</TableCell>
                    <TableCell>
                      <Badge variant={rx.status === "sent" ? "default" : "secondary"} className="capitalize text-xs">
                        {rx.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default SendPrescription;
